import { useEffect, useState, useRef, useCallback } from "react";
import { getRecommendationsV2 } from "../api";
import { filterExpiredV2Cards } from "../utils/recommendations";

// In-flight request dedupe shared by all hook instances, same as useActionCenterData:
// the layout uses this hook for the nav badge count while Performance uses it for the
// Action Center, and both mount around the same time — sharing the pending promise
// avoids fetching the same property twice. Entries are dropped once settled, so later
// mounts fetch fresh data.
const inflightRequests = new Map();

const getRecommendationsV2Shared = (propertyId, force) => {
  if (force || !inflightRequests.has(propertyId)) {
    const request = getRecommendationsV2(propertyId);
    const cleanup = () => {
      if (inflightRequests.get(propertyId) === request) {
        inflightRequests.delete(propertyId);
      }
    };
    request.then(cleanup, cleanup);
    inflightRequests.set(propertyId, request);
  }
  return inflightRequests.get(propertyId);
};

// Refetch broadcast: hook state is per-instance, so a refetch on Performance's
// instance (e.g. after a promo is created from a card) would otherwise leave
// the layout's instance — the nav badge — showing the stale count until a
// reload. Every mounted instance registers here; notifyRefetch fans a refetch
// out to all of them. Only the FIRST listener forces a fresh network request;
// the rest re-read the shared in-flight promise it just created, so the whole
// fan-out still costs one API call (EO-1834).
const refetchListeners = new Set();

const notifyRefetch = (propertyId) => {
  let isFirst = true;
  refetchListeners.forEach((listener) => {
    listener(propertyId, isFirst);
    isFirst = false;
  });
};

/**
 * Fetches the Action Centre v2 (LLM) cards for the selected property (EO-1217).
 * Mirrors {@link useActionCenterData} but hits the v2 read API, whose payload is
 * AppResponse-wrapped — so the cards live at res.data.data ({ propertyId, cards }).
 */
export const useActionCenterV2Data = (selectedProperty) => {
  const [actionCenterV2Data, setActionCenterV2Data] = useState(null);
  const [actionCenterV2Loading, setActionCenterV2Loading] = useState(false);
  // True once this property's FIRST fetch has finished, success OR failure —
  // `loading`/`data` can't express this: before the fetch effect runs and
  // after a failed fetch both read false/null alike. Count displays (nav
  // badge, Action Center header) wait on both feeds' settled flags before
  // showing a number, so a partial v1-only count never flashes (EO-1834).
  // Deliberately latched: a same-property refetch does NOT reset it — data
  // keeps its previous value during a refetch, so counts hold steady instead
  // of blinking out, then update when the fresh read lands.
  const [actionCenterV2Settled, setActionCenterV2Settled] = useState(false);
  const loadedPropertyRef = useRef(null);

  const fetchData = useCallback(
    // `force` bypasses the already-loaded guard and state reset; `refresh`
    // decides whether to hit the network or re-read the shared in-flight
    // promise (see notifyRefetch). They coincide everywhere except the
    // broadcast fan-out, hence the default.
    (propertyId, { force = false, refresh = force, timeZone = null } = {}) => {
      if (!propertyId) {
        setActionCenterV2Data(null);
        setActionCenterV2Loading(false);
        setActionCenterV2Settled(false);
        loadedPropertyRef.current = null;
        return;
      }

      if (!force && loadedPropertyRef.current === propertyId) {
        return;
      }

      setActionCenterV2Loading(true);
      // Only a property's first load un-settles; see the state comment above.
      if (loadedPropertyRef.current !== propertyId) {
        setActionCenterV2Settled(false);
      }
      let isCancelled = false;

      getRecommendationsV2Shared(propertyId, refresh)
        .then((res) => {
          if (isCancelled) return;
          // Expired cards are dropped here, at the single point every consumer
          // reads through — Action Center cards, nav badge count and banner
          // revenue all stay consistent. Expiry is judged on the hotel's
          // calendar day (its timeZone), not the user's (EO-1704).
          setActionCenterV2Data(
            filterExpiredV2Cards(res?.data?.data ?? null, timeZone)
          );
          loadedPropertyRef.current = propertyId;
        })
        .catch(() => {
          if (isCancelled) return;
          setActionCenterV2Data(null);
        })
        .finally(() => {
          if (!isCancelled) {
            setActionCenterV2Loading(false);
            setActionCenterV2Settled(true);
          }
        });

      return () => {
        isCancelled = true;
      };
    },
    []
  );

  useEffect(() => {
    return fetchData(selectedProperty?.propertyId, {
      timeZone: selectedProperty?.timeZone,
    });
  }, [selectedProperty?.propertyId, selectedProperty?.timeZone, fetchData]);

  // Join the refetch broadcast for as long as this instance is mounted. Each
  // instance only reacts to its own property, so a stray notify for another
  // hotel is a no-op.
  useEffect(() => {
    const listener = (propertyId, shouldRefresh) => {
      if (propertyId === selectedProperty?.propertyId) {
        fetchData(propertyId, {
          force: true,
          refresh: shouldRefresh,
          timeZone: selectedProperty?.timeZone,
        });
      }
    };
    refetchListeners.add(listener);
    return () => {
      refetchListeners.delete(listener);
    };
  }, [selectedProperty?.propertyId, selectedProperty?.timeZone, fetchData]);

  // Refetches EVERY mounted instance (nav badge + Action Center), not just
  // this one — a consumed recommendation must disappear from both counts.
  const refetch = useCallback(() => {
    const propertyId = selectedProperty?.propertyId;
    if (propertyId) {
      notifyRefetch(propertyId);
    }
  }, [selectedProperty?.propertyId]);

  return {
    actionCenterV2Data,
    actionCenterV2Loading,
    actionCenterV2Settled,
    refetch,
  };
};
